import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { useParagrapher } from '@/hooks/useParagrapher';
import { Paragraph } from './styled';

interface Props {
  content?: string;
  handleChange?: (short: boolean) => void;
  truncatedLength?: number;
  defaultToOpen?: boolean;
  linkStyle?: 'softLink' | 'strongLink';
}

const CopyTruncator = ({
  handleChange,
  content = '',
  truncatedLength = 205,
  defaultToOpen = false,
  linkStyle = 'softLink',
}: Props) => {
  const [opened, setOpened] = useState(defaultToOpen);
  const { trackEvent } = useTracking();
  const tracked = useRef(false);
  const safeContentRef = useRef(content.toString());
  const safeContentTruncatedRef = useRef(
    safeContentRef.current.slice(0, truncatedLength)
  );
  const [copy, setCopy] = useState(safeContentTruncatedRef.current);
  const handleClick = useCallback(() => {
    if (tracked.current) {
      trackEvent({
        action: 'click',
        modifier: 'show_more',
      });
      tracked.current = true;
    }
    setOpened(!opened);
    handleChange && handleChange(opened);
  }, [tracked, trackEvent, handleChange, opened]);
  useEffect(() => {
    if (safeContentRef.current !== content.toString()) {
      safeContentRef.current = content.toString();
      safeContentTruncatedRef.current = safeContentRef.current.slice(
        0,
        truncatedLength
      );
    }
    setCopy(opened ? safeContentRef.current : safeContentTruncatedRef.current);
  }, [opened, content, truncatedLength]);
  const paragraphs = useParagrapher(copy);
  return (
    <p itemProp="description">
      <span data-cy-id="copy-truncator-content">
        {paragraphs.map((paragraph, index) => (
          <Paragraph expanded={opened} key={index}>
            {paragraph}
          </Paragraph>
        ))}
      </span>
      {safeContentRef.current.length > truncatedLength && opened === false ? (
        <span>
          <span>... </span>
          <Button
            styleType={linkStyle}
            onClick={handleClick}
            data-cy-id="copy-truncator-show-more"
          >
            Show more
          </Button>
        </span>
      ) : safeContentRef.current.length > truncatedLength ? (
        <span>
          {` `}
          <Button
            styleType={linkStyle}
            onClick={handleClick}
            data-cy-id="copy-truncator-show-less"
          >
            Show less
          </Button>
        </span>
      ) : null}
    </p>
  );
};

export default CopyTruncator;
